import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../AuthContext';
import { LogOut, Bell, ChevronUp, User } from 'lucide-react';
import { cn } from '../../utils';

/**
 * ProfileMenu — bottom-left user chip → click → popover.
 *
 * The chip in the sidebar footer is a button, not a passive
 * element. Clicking it toggles a popover anchored above the chip
 * with: the user's display info, a "Notification preferences"
 * shortcut, and a "Sign out" action. Clicking outside or pressing
 * Escape closes the popover.
 *
 * Rendered once inside the desktop sidebar footer. The mobile
 * bottom nav (where this component isn't mounted) keeps the
 * old inline layout because popovers behave poorly on small
 * viewports.
 */
export function ProfileMenu() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleLogout = useCallback(async () => {
    setOpen(false);
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  }, [logout, navigate]);

  const goToPrefs = useCallback(() => {
    setOpen(false);
    navigate('/notifications?tab=preferences');
  }, [navigate]);

  const roleLabel = user?.role
    ? t(`profile.role.${user.role}`, { defaultValue: user.role })
    : '';

  return (
    <div className="relative" ref={rootRef} data-testid="profile-menu-root">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center gap-3 px-2 py-1.5 rounded-xl text-left transition-colors',
          'hover:bg-white/[0.04]',
          open && 'bg-white/[0.06]'
        )}
        data-testid="profile-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-300 text-[#0a1208] flex items-center justify-center font-display text-sm shrink-0">
          {(user?.name || '?').charAt(0).toUpperCase()
        }</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[var(--color-text)] truncate">{user?.name || '—'}</p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] truncate">
            {user?.email || '—'}
          </p>
        </div>
        <ChevronUp
          className={cn('w-4 h-4 text-[var(--color-text-muted)] transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Profile menu"
          className="absolute bottom-full left-0 right-0 mb-2 rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.96)] backdrop-blur-xl shadow-2xl overflow-hidden"
          data-testid="profile-menu-popover"
        >
          <div className="p-4 border-b border-[var(--color-border)]">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-200">
              {roleLabel || t('profile.signedInAs', { defaultValue: 'Signed in as' })}
            </p>
            <p className="mt-1 text-sm text-[var(--color-text)] truncate">{user?.name}</p>
            <p className="text-xs text-[var(--color-text-muted)] truncate">{user?.email}</p>
          </div>
          <div className="p-1">
            <button
              type="button"
              onClick={goToPrefs}
              role="menuitem"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[var(--color-text)] hover:bg-white/[0.04] transition-colors"
              data-testid="profile-menu-preferences"
            >
              <Bell className="w-4 h-4 text-[var(--color-text-muted)]" />
              <span className="flex-1 text-left">
                {t('profile.notificationPreferences', { defaultValue: 'Notification preferences' })}
              </span>
            </button>
            <button
              type="button"
              onClick={handleLogout}
              role="menuitem"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04] transition-colors"
              data-testid="profile-menu-logout"
            >
              <LogOut className="w-4 h-4" />
              <span className="flex-1 text-left">
                {t('auth.logout', { defaultValue: 'Sign out' })}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileMenu;
